import { createElement, FunctionComponent, useMemo } from "react";
import { Controller, FieldError, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { Select } from "../../../../../../components/forms/select";
import { TextField } from "../../../../../../components/forms/text";
import { Grid } from "../../../../../../components/layout";
import { Field } from "../../../../../../store/launch-configurations/models/internal/field";
import MultiTextField from "../../../common/multi-lingual-text";
import DateCustomField from "./date";
import DropDownCustomField from "./drop-down";
import NumberCustomField from "./number";
import TextCustomField from "./text";
import ToggleCustomField from "./toggle";
import FileCustomField from "./file";

interface ICustomFieldsProps {}

const fieldTypes = [
  ["DROP_DOWN", "Drop down"],
  ["MULTI_SELECT", "Multi select"],
  ["MULTI_CHECKBOX", "Multi checkbox"],
  ["SWITCH", "Switch"],
  ["RADIO", "Radio"],
  ["TEXT", "Text"],
  ["DATE", "Date"],
  ["NUMBER", "Number"],
  ["FILE_UPLOAD", "File"],
];

const StyledGrid = styled(Grid)`
  min-width: 20em;
`;

const CustomFields: FunctionComponent<ICustomFieldsProps> = () => {
  const { control, watch } = useFormContext<Field.Field<string>>();

  const type = (watch("type") as any) as Exclude<
    Field.Field<string>,
    Field.DefaultField
  >["type"];

  const configElement = useMemo(() => {
    if (type === "TEXT") return TextCustomField;
    if (type === "SWITCH") return ToggleCustomField;
    if (type === "NUMBER") return NumberCustomField;
    if (type === "DATE") return DateCustomField;
    if (type === "FILE_UPLOAD") return FileCustomField;
    if (["DROP_DOWN", "MULTI_SELECT", "MULTI_CHECKBOX", "RADIO"].includes(type))
      return DropDownCustomField;
  }, [type]);

  return (
    <div style={{ padding: "2em" }}>
      <StyledGrid col-xs={2}>
        <strong>Field</strong>
        <Controller
          control={control}
          name="field"
          render={({ field, fieldState: { error } }) => (
            <TextField {...field} helperText={error?.message} error={!!error} />
          )}
        />

        <strong>Title</strong>
        <Controller
          control={control}
          name="title"
          defaultValue={{}}
          render={({ field, fieldState: { error } }) => (
            <MultiTextField
              {...field}
              error={(error as any) as Record<string, FieldError> | undefined}
            />
          )}
        />
        <strong>Description</strong>
        <Controller
          control={control}
          name="desc"
          defaultValue={{}}
          render={({ field, fieldState: { error } }) => (
            <MultiTextField
              {...field}
              error={(error as any) as Record<string, FieldError> | undefined}
            />
          )}
        />

        <strong>Type</strong>
        <Controller
          control={control}
          name="type"
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              helperText={error?.message}
              error={!!error}
              options={fieldTypes.map(([value, title]) => ({
                title,
                value,
              }))}
            />
          )}
        />

        {configElement ? (
          <>
            <strong>Details</strong>

            <Controller
              control={control}
              name="typeDetails"
              render={({ field, fieldState: { error } }) =>
                createElement(configElement, { field, error })
              }
            />
          </>
        ) : null}
      </StyledGrid>
    </div>
  );
};

export default CustomFields;
